import { useCallback, useState } from 'react';

interface UseStepperActionParams {
  defaultIndex?: number;
  totalSteps: number;
  onNextStep?: () => void;
  onBackStep?: () => void;
}

interface UseStepperActionReturn {
  currentIndex: number;
  canMovePrev: boolean;
  canMoveNext: boolean;
  moveNext: () => void;
  movePrev: () => void;
  moveIndex: (step: number) => void;
  resetStepper: () => void;
  setCurrentIndex: (index: number) => void;
}

/**
 * Generic hook for managing stepper navigation state and actions
 *
 * @param params - Configuration object
 * @param params.defaultIndex - Initial step index (default: 0)
 * @param params.totalSteps - Total number of steps in the stepper
 * @param params.onNextStep - Callback when moving to next step
 * @param params.onBackStep - Callback when moving to previous step
 *
 * @returns Stepper state and navigation functions
 */
const useStepperAction = ({
  defaultIndex = 0,
  totalSteps,
  onNextStep,
  onBackStep,
}: UseStepperActionParams): UseStepperActionReturn => {
  const [currentIndex, setCurrentIndex] = useState(defaultIndex);

  const canMoveNext = currentIndex < totalSteps - 1;
  const canMovePrev = currentIndex > 0;

  const resetStepper = useCallback(() => {
    setCurrentIndex(defaultIndex);
  }, [defaultIndex]);

  const moveIndex = useCallback((step: number) => {
    setCurrentIndex((prevIndex) => prevIndex + step);
  }, []);

  const moveNext = useCallback(() => {
    if (canMoveNext) {
      setCurrentIndex((prev) => prev + 1);
      onNextStep?.();
    }
  }, [canMoveNext, onNextStep]);

  const movePrev = useCallback(() => {
    if (canMovePrev) {
      setCurrentIndex((prev) => prev - 1);
      onBackStep?.();
    }
  }, [canMovePrev, onBackStep]);

  return {
    currentIndex,
    canMovePrev,
    canMoveNext,
    moveNext,
    movePrev,
    moveIndex,
    resetStepper,
    setCurrentIndex,
  };
};

export default useStepperAction;
